import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { catchError, forkJoin, from, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UserAvatarService {
  private readonly baseUrl = environment.baseUrl;
  private readonly avatarCache = new Map<string, string | null>();
  private readonly inFlight = new Map<string, Observable<string | null>>();

  constructor(private readonly http: HttpClient) {}

  private readonly avatarRequestHeaders = new HttpHeaders({
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  });

  getAvatarDataUrl(ownerId: string): Observable<string | null> {
    const normalizedOwnerId = (ownerId || '').trim();
    if (!normalizedOwnerId) {
      return of(null);
    }

    const cacheHit = this.avatarCache.get(normalizedOwnerId);
    if (cacheHit !== undefined) {
      // Return cached value without automatic revalidation on every bind/render.
      // This avoids repeated GET /User/avatar/{id} calls when large dashboard lists rerender.
      return of(cacheHit);
    }

    return this.fetchAvatarDataUrl(normalizedOwnerId);
  }

  setAvatarDataUrl(ownerId: string, dataUrl: string | null): void {
    const normalizedOwnerId = (ownerId || '').trim();
    if (!normalizedOwnerId) {
      return;
    }

    this.inFlight.delete(normalizedOwnerId);
    this.avatarCache.set(normalizedOwnerId, dataUrl);
  }

  invalidateAvatar(ownerId: string): void {
    const normalizedOwnerId = (ownerId || '').trim();
    if (!normalizedOwnerId) {
      return;
    }

    this.inFlight.delete(normalizedOwnerId);
    this.avatarCache.delete(normalizedOwnerId);
  }

  private fetchAvatarDataUrl(ownerId: string): Observable<string | null> {
    const running = this.inFlight.get(ownerId);
    if (running) {
      return running;
    }

    const request$ = this.http
      .get(`${this.baseUrl}/User/avatar/${encodeURIComponent(ownerId)}`, {
        responseType: 'blob',
        headers: this.avatarRequestHeaders,
      })
      .pipe(
        catchError(() => of(null)),
        switchMap((blob) => {
          if (!blob || blob.size === 0) {
            return of(null);
          }
          const t = (blob.type || '').toLowerCase();
          if (t.includes('json') || t.includes('text/html')) {
            return of(null);
          }
          return from(this.blobToDataUrl(blob));
        }),
      );

    const shared$ = request$.pipe(
      catchError(() => of(null)),
      tap(() => this.inFlight.delete(ownerId)),
      map((value) => {
        this.avatarCache.set(ownerId, value);
        return value;
      }),
      shareReplay(1),
    );

    this.inFlight.set(ownerId, shared$);
    return shared$;
  }

  getAvatarsDataUrlMap(ownerIds: string[]): Observable<Record<string, string>> {
    const uniqueIds = Array.from(new Set((ownerIds || []).map((id) => (id || '').trim()).filter(Boolean)));
    if (!uniqueIds.length) {
      return of({});
    }

    const requests = uniqueIds.map((id) =>
      this.getAvatarDataUrl(id).pipe(map((avatar) => ({ id, avatar }))),
    );

    return forkJoin(requests).pipe(
      map((entries) => {
        const mapResult: Record<string, string> = {};
        entries.forEach(({ id, avatar }) => {
          if (avatar) {
            mapResult[id] = avatar;
          }
        });
        return mapResult;
      }),
    );
  }

  private blobToDataUrl(blob: Blob): Promise<string | null> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  }
}
